import React, { useState, useEffect, useRef } from 'react';
import { Menu, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
}

const sections: Section[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'problem', title: 'Problem & Insight' },
  { id: 'solution', title: 'Solution & Process' },
  { id: 'results', title: 'Results & Impact' }
];


function Bundles() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -60% 0px',
        threshold: [0, 0.3, 0.5, 0.7, 1]
      }
    );

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="w-full h-[224px] md:h-[280px] lg:h-[336px] overflow-hidden">
        <img
          src="/skillzlogo.png"
          alt="Bundled Offers"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex relative">
        {isSidebarOpen && (
          <aside className="sticky top-0 h-screen w-80 bg-zinc-950 border-r border-white/10 overflow-y-auto">
            <div className="p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Link
                    to="/"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Home size={18} />
                  </Link>
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Menu size={18} />
                  </button>
                </div>
                <div className="text-white text-sm">Bundled Offers</div>
                <nav className="space-y-1">
                  {sections.map(({ id, title }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-all ${
                        activeSection === id
                          ? 'text-blue-500 border-l-2 border-blue-500'
                          : 'text-white/60 hover:text-white border-l-2 border-transparent'
                      }`}
                    >
                      {title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>
        )}

        {!isSidebarOpen && (
          <div className="sticky top-0 h-fit pt-6 pl-6">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-900/90 backdrop-blur-xl text-white/80 hover:bg-zinc-800 hover:text-white transition-all border border-white/10"
            >
              <Menu size={18} />
            </button>
          </div>
        )}

        <main className={`flex-1 max-w-6xl p-4 py-20 transition-all duration-300 ${isSidebarOpen ? 'ml-auto mr-32' : 'mx-auto'}`}>
          <div className="space-y-20">

            <section id="overview" className="space-y-6 scroll-mt-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Overview</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Skillz is the leading mobile gaming platform that connects players and developers through fun and fair competition.
                </p>
                <p>
                  As PM, I led the strategy and configuration of Bundled Offers — defining bundle compositions, currency ratios, and offer tactics — a net-new monetization framework built on top of Skillz's existing deposit offers. The goal was to move beyond flat bonus cash offers and create multi-currency bundles that drove higher deposit conversion while reducing the platform's over-reliance on bonus cash.
                </p>
              </div>

            </section>

            <section id="problem" className="space-y-6 scroll-mt-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Problem & Insight</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Skillz's deposit offers were structured as a straightforward bonus cash calculation — deposit X, get Y bonus cash. This created two compounding problems: margins were being squeezed by high bonus cash payouts, and offers felt generic with nothing to differentiate or excite users.
                </p>
                <p className="font-medium text-white/90">Business Impact:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Over-reliance on bonus cash was eroding margins</li>
                  <li>Offers lacked perceived variety or value, limiting engagement</li>
                  <li>Z's (free-to-play currency) and Ticketz (loyalty currency) were underutilized as monetization levers</li>
                </ul>
                <p className="font-medium text-white/90 pt-4">What I Found:</p>
                <p>
                  Through <strong className="text-white/90">competitive analysis</strong> across 15+ platforms and <strong className="text-white/90">analytics</strong> deep-dives, I identified that:
                </p>
                <ol className="list-decimal list-inside space-y-2 pl-4">
                  <li><strong className="text-white/90">Flat offers don't convert</strong> — Users respond better to bundled value that feels like they're "getting more"</li>
                  <li><strong className="text-white/90">Z's had a perception problem</strong> — Players didn't inherently see Z's as valuable, making them a weak incentive on their own</li>
                  <li><strong className="text-white/90">Missed margin opportunity</strong> — Z's and Ticketz carry low actual cost but high perceived value when packaged correctly</li>
                </ol>
              </div>
            </section>

            <section id="solution" className="scroll-mt-20 space-y-8">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Solution & Process</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Built a new bundled offers framework from the ground up, layering on top of the existing store structure to introduce two bundle types:
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10" style={{ borderWidth: '1px' }}>
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-4 px-4 text-white font-medium">Bundle Type</th>
                      <th className="text-left py-4 px-4 text-white font-medium">Composition</th>
                      <th className="text-left py-4 px-4 text-white font-medium">Why This Worked</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70 text-sm leading-relaxed">
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4 align-top font-medium text-white/90">Cash + Bonus Cash + Z's</td>
                      <td className="py-4 px-4 align-top">Real cash deposit matched with bonus cash and Z's</td>
                      <td className="py-4 px-4 align-top">Z's added perceived value at near-zero margin cost, reducing bonus cash dependence</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4 align-top font-medium text-white/90">Cash + Bonus Cash + Ticketz</td>
                      <td className="py-4 px-4 align-top">Real cash deposit matched with bonus cash and loyalty Tickets</td>
                      <td className="py-4 px-4 align-top">Tied deposits to loyalty progression, increasing retention incentive</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="space-y-4 text-white/70 leading-relaxed pt-6">
                <p className="font-medium text-white/90 text-lg">My Process</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>
                    <strong className="text-white/90">Research & Competitive Analysis</strong> — Conducted competitive analysis across 15+ gaming and fintech apps to understand bundling and perceived value mechanics
                  </li>
                  <li>
                    <strong className="text-white/90">Analytics Modeling & Bundle Strategy</strong> — Partnered with analytics to model bundle compositions, optimizing the bonus cash-to-deposit ratio while maximizing perceived value
                  </li>
                  <li>
                    <strong className="text-white/90">Monetization Strategy</strong> — Designed a multi-currency bundle framework that shifted value perception away from bonus cash toward Z's and Ticketz
                  </li>
                  <li>
                    <strong className="text-white/90">Execution & Product Documentation</strong> — Owned end-to-end delivery as solo PM; authored PRDs, bundle specs, and QA documentation coordinating across engineering, design, and QA
                  </li>
                  <li>
                    <strong className="text-white/90">A/B Testing & Post-Launch Optimization</strong> — Ran A/B tests to validate bundle performance; iterated on Z's positioning after discovering users undervalued them at launch
                  </li>
                </ul>
              </div>
            </section>

            <section id="results" className="space-y-6 scroll-mt-20 pb-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Results & Impact</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Measurably reduced bonus cash reliance, improving deposit margins</li>
                  <li>Drove higher deposit conversion rates vs. control group</li>
                  <li>Improved offer engagement across bundled SKUs</li>
                  <li>Successfully increased perceived value of Z's and Ticketz as monetization currencies</li>
                  <li>Generated meaningful incremental annual revenue through improved bundle economics</li>
                </ul>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Bundles;
